// Cash rounding rule picker (ops-simulation T07): a step in minor units and a
// method. Shared by Settings › Tax (tenant default), a location's settings
// (override) and the sales invoice dialog (per-invoice rule).

import { Select } from '@bbpos/ui';
import {
  amountToMajor,
  isValidCashRoundingStep,
  roundToCashStep,
  type CashRoundingMode,
  type CashRoundingRule,
} from '@bbpos/utils';

/** Candidate steps; only those dividing one major unit of the currency are offered. */
const CANDIDATE_STEPS = [1, 5, 10, 25, 50, 100, 250, 500, 1000];

export function cashRoundingStepOptions(currency: string) {
  return CANDIDATE_STEPS.filter((s) => isValidCashRoundingStep(s, currency)).map((s) => ({
    value: String(s),
    label: s === 1 ? 'No rounding (exact)' : `${amountToMajor(s, currency)} ${currency}`,
  }));
}

export const CASH_ROUNDING_MODE_OPTIONS: Array<{ value: CashRoundingMode; label: string }> = [
  { value: 'HALF_UP', label: 'Nearest (half up)' },
  { value: 'UP', label: 'Always up' },
  { value: 'DOWN', label: 'Always down' },
];

/** "0.050 OMR, nearest" — a one-line summary of a rule. */
export function describeCashRounding(rule: CashRoundingRule, currency: string): string {
  if (rule.step <= 1) return 'No rounding';
  const mode = CASH_ROUNDING_MODE_OPTIONS.find((m) => m.value === rule.mode)?.label.toLowerCase() ?? 'nearest';
  return `${amountToMajor(rule.step, currency)} ${currency}, ${mode}`;
}

interface CashRoundingFieldsProps {
  currency: string;
  value: CashRoundingRule;
  onChange: (rule: CashRoundingRule) => void;
  /** Sample amount (minor units) shown as "5.237 → 5.250". */
  example?: number;
  disabled?: boolean;
}

export function CashRoundingFields({ currency, value, onChange, example, disabled }: CashRoundingFieldsProps) {
  const sample = example ?? 0;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select
          label="Round to"
          options={cashRoundingStepOptions(currency)}
          value={String(value.step)}
          disabled={disabled}
          onChange={(v) => onChange({ ...value, step: Number(typeof v === 'string' ? v : v[0]) || 1 })}
        />
        <Select
          label="Method"
          options={CASH_ROUNDING_MODE_OPTIONS}
          value={value.mode}
          disabled={disabled || value.step <= 1}
          onChange={(v) => onChange({ ...value, mode: (typeof v === 'string' ? v : v[0]) as CashRoundingMode })}
        />
      </div>
      {sample > 0 && value.step > 1 && (
        <p className="text-xs text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/20 rounded-lg px-3 py-2 tabular-nums">
          Example: {amountToMajor(sample, currency)} → {amountToMajor(roundToCashStep(sample, value), currency)} {currency}
        </p>
      )}
    </div>
  );
}
